import { Col, Row } from 'antd';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef, useState } from 'react';
import Forms from '../form/form';
import DrawerSettings from '../settings/drawer-settings';
import { IMapboxStyleLayers, useMapboxContext } from './context/mapbox-context';
import DrawerData from './data/drawer-data';
import ButtonMarker from './tools/button-marker';
import MapboxSearch from './tools/mapbox-search';
import ButtonFilter from './tools/button-filter';
import DrawerFilter from './tools/drawer-filter';
import { forEachChild } from 'typescript';


const Mapbox = () => {
    const { style, styleLayers, coordinates, lotArea, setStyleLayers, setClickProperties, setIsOpenDataDrawer } = useMapboxContext();
    mapboxgl.accessToken = 'pk.eyJ1IjoibGFnYXJzb2Z0IiwiYSI6ImNsajh4a3owNDB0cWMzZW5xOG55Nml3NzkifQ.wsKGApOwDpcS15lme4MB0g';

    const mapContainer: any = useRef(null);
    const map: any = useRef(null);
    const [zoom, setZoom] = useState(12);
    const [initialState, setInitialState] = useState<boolean>(true);
    const [marker, setMarker] = useState<mapboxgl.Marker | null>();

    useEffect(() => {
        if (map.current) return; // initialize map only once
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: style,
            center: [coordinates.lng, coordinates.lat],
            zoom: zoom,
        });

    }, [map, mapContainer])

    //If style change, update mapbox style
    useEffect(() => {
        if (!style) return

        map.current.setStyle(style);

        map.current.once('style.load', () => {
            // Access the map's style
            const mapStyle = map.current.getStyle();
            // Access the components in the style
            const layers = mapStyle.layers
                .filter((layer: any) => layer.type === 'symbol' || layer.type === 'line' || layer.type === 'fill')
                .map((layer: any) => ({
                    layerId: layer.id,
                    visible: true
                }));

            setStyleLayers(layers)
        });

        // Used to get lot data
        map.current.on('click', ['pluto-merged-bbl-lot-0-2023-c-bvewzx', 'pluto-merged-bbl-lot-1-2023-c-cpxpqs', 'pluto-merged-bbl-lot-2-2023-c-2r9w6c', 'pluto-merged-bbl-lot-3-2023-c-7hbkng', 'pluto-merged-bbl-lot-4-2023-c-dbycfl', 'pluto-merged-bbl-lot-6-2023-c-b7aygz', 'pluto-merged-bbl-lot-5-2023-c-60sq3r'], (e: { point: any; }) => {
            const features = map.current.queryRenderedFeatures(e.point, { layers: ['pluto-merged-bbl-lot-0-2023-c-bvewzx', 'pluto-merged-bbl-lot-1-2023-c-cpxpqs', 'pluto-merged-bbl-lot-2-2023-c-2r9w6c', 'pluto-merged-bbl-lot-3-2023-c-7hbkng', 'pluto-merged-bbl-lot-4-2023-c-dbycfl', 'pluto-merged-bbl-lot-5-2023-c-60sq3r', 'pluto-merged-bbl-lot-6-2023-c-b7aygz'] });
            if (features.length > 0) {
                const lot = features[0];
                // Set data into context and open drawer
                setClickProperties(lot.properties);
                setIsOpenDataDrawer(true);

                // Highlight lot
                const existingLayer = map.current.getLayer('pluto-highlighted');
                if (existingLayer) {
                    map.current.setFilter('pluto-highlighted', [
                        'in',
                        'BBL',
                        lot.properties.BBL
                    ]);
                } else {
                    map.current.addLayer({
                        id: 'pluto-highlighted',
                        type: 'fill',
                        source: lot.layer.source,
                        'source-layer': lot.layer['source-layer'],
                        paint: {
                            'fill-outline-color': '#484896',
                            'fill-color': '#6e599f',
                            'fill-opacity': 0.75
                        },
                        filter: ['in', 'BBL', lot.properties.BBL]
                    });
                }
            }
        });

    }, [style])

    const addLayerWithFilter = (id: string, source: string, layer: string) => {
        map.current.addLayer({
            id: id,
            type: 'fill',
            source: source,
            'source-layer': layer,
            minzoom: 13,
            paint: {
                'fill-outline-color': '#484896',
                'fill-color': '#F7B50C',
                'fill-opacity': 0.75
            },
            filter: ['in', 'LotArea', lotArea]
        });
    }

    const highlightLotForArea = (existsLayerName: string, newLayerName: string) => {
        const existsLayer = map.current.getLayer(existsLayerName);
        if (existsLayer) {
            const existingLayer = map.current.getLayer(newLayerName);
            if (!existingLayer) {
                addLayerWithFilter(newLayerName, existsLayer.source, existsLayer.sourceLayer)
            }
        }
        map.current.setFilter(newLayerName, [
            'in',
            'LotArea',
            lotArea
        ]);
    }

    useEffect(() => {
        if (lotArea === -1) return
        highlightLotForArea('pluto-merged-bbl-lot-0-2023-c-bvewzx', "pluto-highlighted-0")
        highlightLotForArea('pluto-merged-bbl-lot-1-2023-c-cpxpqs', "pluto-highlighted-1")
        highlightLotForArea('pluto-merged-bbl-lot-2-2023-c-2r9w6c', "pluto-highlighted-2")
        highlightLotForArea('pluto-merged-bbl-lot-3-2023-c-7hbkng', "pluto-highlighted-3")
        highlightLotForArea('pluto-merged-bbl-lot-4-2023-c-dbycfl', "pluto-highlighted-4")
        highlightLotForArea('pluto-merged-bbl-lot-6-2023-c-b7aygz', "pluto-highlighted-5")
        highlightLotForArea('pluto-merged-bbl-lot-5-2023-c-60sq3r', "pluto-highlighted-6")
    }, [lotArea])


    const handleStyleLayer = () => {
        styleLayers.forEach((layer: IMapboxStyleLayers) => {
            map.current.setLayoutProperty(layer.layerId, 'visibility', layer.visible ? 'visible' : 'none');
        });
    };

    useEffect(() => {
        if (map.current.loaded()) {
            handleStyleLayer();
        } else {
            map.current.on('load', handleStyleLayer);
        }
    }, [styleLayers])


    const removeMarker = () => {
        setMarker(null)
        marker?.remove();
    }

    useEffect(() => {
        if (!map.current) return
        if (initialState) return setInitialState(false)

        const marker = new mapboxgl.Marker()

        //Remove provious markers
        removeMarker()

        // Move to cordinates
        map.current.flyTo({
            center: [coordinates.lng, coordinates.lat],
            zoom: 16,
            duration: 1,
            essential: false
        });

        //Add new marker
        marker.setLngLat([coordinates.lng, coordinates.lat])
        marker.addTo(map.current)

        //Save Marker in useState
        setMarker(marker)

    }, [coordinates])

    return (
        <>
            <div style={{ position: "fixed", bottom: 100, right: 50, zIndex: 2, }}><Forms /></div>
            <div style={{ position: "absolute", zIndex: 2, width: "100%" }}>
                <Row>
                    <Col span={8}><MapboxSearch /></Col>
                    {marker && <Col span={1}><ButtonMarker removeMarker={removeMarker} /></Col>}
                    <Col offset={14}><ButtonFilter /></Col>

                </Row>
            </div>
            <DrawerSettings />
            <DrawerFilter />


            <DrawerData />
            <div ref={mapContainer} style={{ height: "100%", zIndex: 1 }}></div>
        </>
    );
}

export default Mapbox;